import axios from "axios";

export const linkImage = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/'

export const linkAPI = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '')

const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/",
  timeout: 15000, // Default 15s timeout
});

// Centralized authentication cleanup
const clearAuthAndRedirect = () => {
  localStorage.removeItem("ACCESS_TOKEN");
  localStorage.removeItem("REFRESH_TOKEN");
  localStorage.removeItem("role");
  localStorage.removeItem("CURRENT_BUSINESS");
  localStorage.removeItem("tokenExpirationTime");
  localStorage.removeItem("refreshTimeAdmin");
  
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

// ── Request interceptor: attach Bearer token and Accept-Language ───────────────
axiosClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("ACCESS_TOKEN");
  const lang = localStorage.getItem("i18nextLng") || "fr"; // Default to fr if not set

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers["Accept-Language"] = lang;

  // Extend timeout for file uploads and blob downloads
  if (config.data instanceof FormData || config.responseType === 'blob') {
    config.timeout = 120000; // 2 minutes
  }

  return config;
});

// ── Response interceptor: handle 401 with automatic token refresh ─────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosClient.interceptors.response.use(
  (response) => {
    if (response.data && response.data.error === "Client is disabled." && response.data.etat === false) {
      clearAuthAndRedirect();
    }
    return response;
  },
  async (error) => {
    // 1. Handle cancelled requests silently
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // 2. Distinguish Timeout and Network Errors
    if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
      error.isTimeoutError = true;
    } else if (!error.response && !error.isTimeoutError) {
      error.isNetworkError = true;
    }

    const originalRequest = error.config;

    if (error.response) {
      let data = error.response.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          data = JSON.parse(text);
        } catch (e) {
          // not JSON or not parseable
        }
      }
      if (
        data &&
        (data.error === "Client is disabled." ||
          data.message === "Votre compte est désactivé" ||
          (data.error === "Client is disabled." && data.etat === false))
      ) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem("REFRESH_TOKEN");

      // No refresh token available → logout immediately
      if (!refreshToken) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue calls while a refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return axiosClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${axiosClient.defaults.baseURL}token/refresh/`, {
          refresh: refreshToken,
        });

        const newAccessToken = response.data.access;
        localStorage.setItem("ACCESS_TOKEN", newAccessToken);
        if (response.data.refresh) {
          localStorage.setItem("REFRESH_TOKEN", response.data.refresh);
        }

        axiosClient.defaults.headers["Authorization"] = `Bearer ${newAccessToken}`;
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        return axiosClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Refresh failed → force logout
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosClient;
